import {
  FieldValue,
  FieldValues,
  FieldValueSource,
  OntologyField,
} from 'features/libra/ontology/ontologyFieldTypes'

export const isFieldValueMissing = (value: unknown): boolean => {
  if (typeof value === 'string') {
    return value.length === 0
  }
  return value === undefined || value === null
}

/**
 * Unwraps a field value from { value, source } to just the inner value.
 * Specify a generic to type the return value
 */
export const unwrapFieldValue = <T = unknown>(
  fieldValue: FieldValue | undefined
): T | undefined => {
  return fieldValue?.value as T | undefined
}

/**
 * Wraps a value with source metadata.
 * The generic is inferred from the input
 */
export const wrapFieldValue = <T = unknown>(
  value: T,
  source: FieldValueSource = null
): FieldValue<T> => {
  return { value, source }
}

/**
 * Wraps each field in a record individually with { value, source: null }.
 */
export const wrapFields = (record: Record<string, unknown>): FieldValues =>
  Object.fromEntries(
    Object.entries(record).map(([key, val]) => [key, wrapFieldValue(val)])
  )

/**
 * Gets the source from a field value.
 * Returns null if the field value is missing.
 */
export const getFieldSource = (
  fieldValue: FieldValue | undefined
): FieldValueSource => {
  return fieldValue?.source ?? null
}

export const getValueWithDefault = (
  value: FieldValue | undefined,
  fieldDefinition: OntologyField
): FieldValue | undefined => {
  const actualValue = unwrapFieldValue(value)

  if (
    isFieldValueMissing(actualValue) &&
    fieldDefinition.defaultValue !== undefined
  ) {
    return wrapFieldValue(fieldDefinition.defaultValue, null)
  }

  return value
}

/**
 * Merges an incoming FieldValues patch into a previous FieldValues map.
 *
 * Shallow-merges each field's wrapper so any keys on `previous[id]` that
 * the patch omits are preserved. The canonical example is `llmValue`
 * (immutable LLM-suggestion lineage): FE edit handlers produce
 * `{ value, source }` wrappers via `wrapFieldValue`, which don't re-emit
 * `llmValue`, and Rolodex's patch diff would otherwise emit a
 * `delete_data` op for the missing key — silently dropping the lineage
 * on every user edit of an autofilled field.
 *
 * Any future key added to `FieldValue` inherits the same "sticky unless
 * overridden" semantics without touching this helper.
 *
 * Note: explicit `undefined` in a patch wrapper counts as "present" and
 * will shadow prev. No current call site does this; use `null` (or a
 * dedicated helper) if you ever need to clear a key.
 */
export const mergeFieldValues = (
  previous: FieldValues,
  patch: FieldValues
): FieldValues => {
  const merged: FieldValues = { ...previous }
  for (const [fieldId, next] of Object.entries(patch)) {
    merged[fieldId] = { ...previous[fieldId], ...next }
  }
  return merged
}

export const collectDefaultsForFields = (
  fieldValues: FieldValues,
  ontologyFields: Record<string, OntologyField>,
  fields: ReadonlyArray<string>
): Partial<FieldValues> => {
  return fields.reduce<Partial<FieldValues>>((acc, fieldId) => {
    const fieldDefinition = ontologyFields[fieldId]
    if (!fieldDefinition || fieldDefinition.defaultValue === undefined) {
      return acc
    }
    const currentValue = fieldValues?.[fieldId]
    const actualValue = unwrapFieldValue(currentValue)

    if (isFieldValueMissing(actualValue)) {
      acc[fieldId] = wrapFieldValue(fieldDefinition.defaultValue, null)
    }
    return acc
  }, {})
}
